/**
 * Lazy load with retry for dynamic imports
 *
 * Wraps React.lazy() with retry logic to handle transient network failures
 * and stale chunks after deployments. Retries up to 3 times with exponential
 * backoff before falling back to the existing chunk error handler.
 */

import { lazy, ComponentType } from 'react';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Creates a lazy-loaded component with automatic retry on failure.
 *
 * @example
 * const DashboardPage = lazyWithRetry(() => import('@/pages/app/dashboard/page'));
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        // Add cache-busting query param on retry attempts
        if (attempt > 0) {
          // Clear module cache by using a timestamped import
          // This helps when the chunk URL is cached but the content is stale
          const result = await componentImport();
          return result;
        }
        return await componentImport();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Check if it's a chunk load error
        const isChunkError =
          lastError.message.includes('Failed to fetch dynamically imported module') ||
          lastError.message.includes('Importing a module script failed') ||
          lastError.message.includes('error loading dynamically imported module') ||
          lastError.message.includes('Loading chunk') ||
          lastError.message.includes('ChunkLoadError');

        if (!isChunkError) {
          // Not a chunk error, don't retry
          throw lastError;
        }

        // Wait before retrying with exponential backoff
        if (attempt < MAX_RETRIES - 1) {
          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAY_MS * Math.pow(2, attempt))
          );
        }
      }
    }

    // All retries failed, throw the last error
    // The chunk error handler in main.tsx will catch this and refresh
    throw lastError;
  });
}

export default lazyWithRetry;
